/**
 * Rule that checks if a screen is assigned to a usertask
 */
const { is } = require("bpmnlint-utils");

function readScreenIdFromProject(project, selectedWfId, stepId) {
  if (!project) return { screenId: "", source: "none" };
  const arr1 = Array.isArray(project.processStepScreenIds) ? project.processStepScreenIds : [];
  const arr2 = Array.isArray(project.processStepScreenInfo) ? project.processStepScreenInfo : [];

  const hit1 = arr1.find((x) => x && x.projectId === project.id && x.processId === selectedWfId && x.stepId === stepId);
  if (hit1 && hit1.screenId) return { screenId: String(hit1.screenId), source: "processStepScreenIds" };

  const hit2 = arr2.find((x) => x && x.projectId === project.id && x.processId === selectedWfId && x.stepId === stepId);
  if (hit2 && hit2.screenId) return { screenId: String(hit2.screenId), source: "processStepScreenInfo" };

  return { screenId: "", source: "none" };
}

function readScreenIdFromExtra(extra) {
  if (!Array.isArray(extra)) return "";
  const hit = extra.find((p) => p && (p.key === "screenId" || p.Key === "screenId"));
  return hit ? String(hit.value ?? hit.Value ?? "") : "";
}

module.exports = function screenAssigned({ ctx }) {
  return {
    check(node, reporter) {
      if (!node) return;
      if (!is(node, "bpmn:UserTask")) return;

      const sd = ctx && ctx.providerInstance ? ctx.providerInstance : null;
      if (!sd) return;

      const workflowProvider = sd.workflowProvider ? sd.workflowProvider : null;
      const ctxValues =
        typeof sd.getScreenDesignContextProviderValues === "function"
          ? sd.getScreenDesignContextProviderValues()
          : null;

      const selectedWf =
        (workflowProvider &&
          typeof workflowProvider.getSelectedWf === "function" &&
          workflowProvider.getSelectedWf()) ||
        (ctxValues && ctxValues.selectedWf) ||
        null;

      const project = (sd && sd.project) || (ctxValues && ctxValues.project) || null;

      // The screen-assignment mapping is keyed by the active process and the BPMN
      // node id, so both the selected workflow and the project must be resolvable
      // before we can make a reliable judgement. When the context is not ready yet
      // we skip; the linter re-runs on the next diagram event once it loads.
      if (!selectedWf || !project) return;

      // The in-memory JSON step is OPTIONAL. Screen assignments live in
      // project.processStepScreenIds keyed by the BPMN node id (node.id) — NOT by
      // the step object — so we must NOT bail out when the step is missing.
      // A user task can exist in the BPMN XML without a matching JSON step
      // (a transient or persisted XML<->JSON desync, e.g. from DSL/AI-generated
      // diagrams, version merges, or imports). The previous `if (!step) return`
      // silently swallowed the "screen missing" warning in exactly those cases.
      // The step is now only used as a secondary source (legacy extraParameters).
      let step = null;
      if (workflowProvider && typeof workflowProvider.findStepRecursively === "function") {
        try {
          step = workflowProvider.findStepRecursively(selectedWf, (s) => s && s.id === node.id);
        } catch {
          step = null;
        }
      }

      if (!step) {
        const steps = ctx && typeof ctx.getWfSteps === "function" ? ctx.getWfSteps() : undefined;
        if (Array.isArray(steps)) {
          step = steps.find((s) => s && s.id === node.id) || null;
        }
      }

      const selectedWfId = selectedWf && selectedWf.id ? selectedWf.id : null;
      const stepId = node.id;

      const projRead = readScreenIdFromProject(project, selectedWfId, stepId);
      const fromProject = projRead.screenId;
      const fromExtra = step ? readScreenIdFromExtra(step.extraParameters) : "";
      const screenId = fromProject || fromExtra;

      if (!screenId) {
        reporter.report(node.id, "User Tasks should have a screen assigned.");
        return;
      }

      // KOA-18199: the PRESENCE of a mapping is not enough — the mapped screen may have
      // been deleted (a UI delete whose fire-and-forget prune failed or was overwritten
      // by a project refresh, an MCP/AI delete which never pruned, another user's
      // delete). "Deleted screen shows a warning" used to be an accidental side effect
      // of the prune; checking EXISTENCE against the live screen list makes it a
      // guarantee. An empty/unloaded list is skipped to avoid boot-time false positives.
      const screenList = ctxValues && Array.isArray(ctxValues.screenList) ? ctxValues.screenList : null;
      if (screenList && screenList.length > 0) {
        const exists = screenList.some((s) => s && String(s.id) === String(screenId));
        if (!exists) {
          reporter.report(node.id, "User Task's assigned screen no longer exists.");
        }
      }
    }
  };
};
